import React from 'react';
import {Divider, IconButton, IconButtonThemes, Text} from '@wix/wix-ui-tpa/cssVars';
import {PlusSmall} from '@wix/wix-ui-icons-common/on-stage';
import {useControllerProps} from '../../../domain/controllers/ControllerContext';
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../domain/specs';
import {Carousel} from '../Carousel/Carousel';
import {PurchaseRecommendationModel} from '../../../domain/models/PurchaseRecommendation.model';
import {shorten} from '../../../domain/utils/shorten';
import {CROSS_SELL_DESCRIPTION_MAX_CHARS, CROSS_SELL_TITLE_MAX_CHARS} from '../../constants';
import {RecommendationImage, SMALL_IMAGE_SIZE} from './RecommendationImage/RecommendationImage';
import {crossSellSkeletonItems} from './CrossSellCardSkeleton/CrossSellCardSkeleton';
import {classes} from './CrossSell.st.css';

export enum CrossSellDataHook {
  root = 'CrossSell.root',
  divider = 'CrossSell.divider',
  title = 'CrossSell.title',
  recommendation = 'CrossSell.recommendation',
  displayName = 'CrossSell.displayName',
  description = 'CrossSell.description',
  price = 'CrossSell.price',
  addToCartButton = 'CrossSell.addToCartButton',
}

export const CrossSell = ({imageSize = SMALL_IMAGE_SIZE}: {imageSize?: number}) => {
  const {
    purchaseRecommendations,
    arePurchaseRecommendationsLoading,
    shouldShowCrossSell,
    applyRecommendation,
    isApplyingRecommendation,
  } = useControllerProps().cartStore;
  const {experiments} = useExperiments();
  const {t} = useTranslation();

  if (
    !experiments.enabled(SPECS.ecomCartCrossSell) ||
    !shouldShowCrossSell ||
    (!arePurchaseRecommendationsLoading && purchaseRecommendations.length === 0)
  ) {
    return null;
  }

  const renderCard = (recommendation: PurchaseRecommendationModel, position: number) => (
    <div data-hook={CrossSellDataHook.recommendation} className={classes.card}>
      <div className={classes.imageContainer}>
        <RecommendationImage recommendation={recommendation} size={imageSize} />
        <IconButton
          data-hook={CrossSellDataHook.addToCartButton}
          className={classes.addToCartButton}
          theme={IconButtonThemes.Box}
          icon={<PlusSmall />}
          aria-label={t('cart.crossSell.addButton.ariaLabel')}
          disabled={isApplyingRecommendation}
          onClick={() => applyRecommendation(recommendation, position)}
        />
      </div>
      <Text data-hook={CrossSellDataHook.displayName} className={classes.displayName}>
        {recommendation.displayName && shorten(recommendation.displayName, CROSS_SELL_TITLE_MAX_CHARS)}
      </Text>
      <Text data-hook={CrossSellDataHook.price} className={classes.price}>
        {recommendation.formattedPrice}
      </Text>
      <Text data-hook={CrossSellDataHook.description} className={classes.description}>
        {recommendation.description && shorten(recommendation.description, CROSS_SELL_DESCRIPTION_MAX_CHARS)}
      </Text>
    </div>
  );

  return (
    <section data-hook={CrossSellDataHook.root} className={classes.root}>
      <Divider className={classes.divider} data-hook={CrossSellDataHook.divider} />
      <Carousel
        itemWidth={imageSize}
        previousArrowAriaLabel={t('cart.crossSell.previousButton.ariaLabel')}
        nextArrowAriaLabel={t('cart.crossSell.nextButton.ariaLabel')}
        title={
          <Text tagName="h3" data-hook={CrossSellDataHook.title} className={classes.title}>
            {t('cart.crossSell.title')}
          </Text>
        }
        arrowsDisabled={arePurchaseRecommendationsLoading}
        items={
          arePurchaseRecommendationsLoading
            ? crossSellSkeletonItems(imageSize)
            : purchaseRecommendations.map((recommendation, position) => ({
                id: recommendation.id,
                content: renderCard(recommendation, position),
              }))
        }
      />
    </section>
  );
};
